import { useState } from "react";
import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import ComponentCard from "../../components/common/ComponentCard";
import Input from "../../components/form/input/InputField";
import Label from "../../components/form/Label";
import Button from "../../components/ui/button/Button";
import { useToast } from "../../context/ToastContext";

import type { SendPasswordResetWithTokenResp, SendPasswordResetWithTokenValues } from '../../types/resetPasswordTypes';

const SEND_PASSWORD_RESET_WITH_TOKEN = gql`
  mutation SendPasswordResetWithToken($email: String!) {
    sendPasswordResetWithToken(email: $email) {
      success
      errors
      message
      alreadySent
    }
  }
`   
   
const ResetPasswordPage = () => {
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [error, setError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [resetPassword] = useMutation<SendPasswordResetWithTokenResp, SendPasswordResetWithTokenValues>(SEND_PASSWORD_RESET_WITH_TOKEN);

  const validateEmail = (value: string) => {
    const isValidEmail =
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
    setError(!isValidEmail);
    setErrorMsg("This is an invalid email address.")
    return isValidEmail;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    validateEmail(value);
  };

  const resetPasswordHandle = async () => {
    try {
      const { data } = await resetPassword({
        variables: { email },
      });
      
      const responseData = data?.sendPasswordResetWithToken;

      if (responseData?.success) {
        toast.success(responseData?.message); 
        if (!responseData?.alreadySent) setEmail('');
      } else {
        if (responseData) toast.error(responseData.message);
        setError(true)
        setErrorMsg(responseData?.errors?.join(", ") ?? "Unknown error");
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setErrorMsg(message);
      setError(true)
      toast.error(message); 
    }
  }
  
  return (
    <div>
      <div className="min-h-screen rounded-2xl border border-gray-200 bg-white px-5 py-7 dark:border-gray-800 dark:bg-white/[0.03] xl:px-10 xl:py-12">
        <div className="mx-auto w-full max-w-[630px] text-center">
          <ComponentCard
            title="Reset password page"
            desc="Page for users to reset their password by entering their email address."
          >        
            <div className="space-y-5 sm:space-y-6">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={email}
                  error={error}
                  onChange={handleEmailChange}
                  placeholder="Enter your email"
                  hint={error ? errorMsg : ""}
                />
              </div>
              <Button 
                size="md" 
                variant="primary"
                disabled={error || email.length === 0}  
                onClick={resetPasswordHandle}
              >
                Reset Password
              </Button></div>
          </ComponentCard>    
        </div>
      </div>
    </div>  
  )
};

export default ResetPasswordPage;
